import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import BackButton from "../components/BackButton";
import ChangePasswordOverlay from "../components/auth/ChangePasswordOverlay";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { fetchCurrentUser, updateProfile } from "../store/auth/authSlice";
import { fetchPlans } from "../store/plans/plansSlice";
import { fetchCurrentSubscription } from "../store/billing/billingSlice";
import {
  createGroceryList,
  fetchGroceryLists,
  clearCreateGroceryListState,
  fetchGroceryListDetail,
  updateGroceryList,
  deleteGroceryList,
  clearUpdateGroceryListState,
  clearDeleteGroceryListState,
  addGroceryProduct,
  updateGroceryProduct,
  deleteGroceryProduct,
  clearAddGroceryProductState,
  clearUpdateGroceryProductState,
  clearDeleteGroceryProductState,
} from "../store/groceryLists/groceryListsSlice";
import { API_BASE_URL, getAuthHeader } from "../api/client";
import { useToast } from "../components/ToastProvider";

const tabs = [
  { id: "public", label: "Public Profile" },
  { id: "billing", label: "Billings & Plans" },
  { id: "grocery", label: "My Grocery Lists" },
];

function SettingsPageStyles() {
  return (
    <style>{`
      /* Settings page UI alignment + colors */
      .settings-page-root {
        display: flex !important;
        flex-direction: column !important;
      }

      .settings-page-root > main {
        max-width: 80rem !important; /* ~max-w-7xl */
        flex: 1 1 auto !important;
        min-height: unset !important;
        width: 100% !important;
      }

      @media (min-width: 768px) {
        .settings-page-root > main {
          padding-left: 2.5rem !important; /* md:px-10 */
          padding-right: 2.5rem !important;
        }
      }
    `}</style>
  );
}

function Settings({ onOpenAuth, onOpenMenu }) {
  const [activeTab, setActiveTab] = useState("public");
  const [groceryView, setGroceryView] = useState("lists"); // lists | create | detail
  const [groceryPrevView, setGroceryPrevView] = useState("lists");
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [selectedGroceryListId, setSelectedGroceryListId] = useState(null);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const token = useAppSelector((s) => s.auth.token);
  const user = useAppSelector((s) => s.auth.user);
  const isLoggedIn = Boolean(token);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    const view = params.get("view");
    if (tab === "grocery") {
      setActiveTab("grocery");
      if (view === "create" || view === "detail" || view === "lists") {
        setGroceryView(view);
      }
    }
  }, [location.search]);

  useEffect(() => {
    if (activeTab === "public" && isLoggedIn && !user) {
      dispatch(fetchCurrentUser());
    }
  }, [activeTab, isLoggedIn, user, dispatch]);

  if (!isLoggedIn) {
    return (
      <div className="settings-page-root min-h-screen flex flex-col bg-femty-bg text-femty-text">
        <SettingsPageStyles />
        <Header onOpenAuth={onOpenAuth} onOpenMenu={onOpenMenu} />
        <main className="mx-auto w-full max-w-7xl flex-1 items-center justify-center px-4 py-40 md:px-10 min-h-[calc(100vh-120px)]">
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white px-8 py-10 text-center shadow-sm justify-self-center">
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-femty-bg">
              <svg
                viewBox="0 0 24 24"
                className="h-8 w-8 text-femty-muted"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path d="M16 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0z" />
                <path d="M12 14a5 5 0 0 0-5 5v2a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-2a5 5 0 0 0-5-5h-2z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-femty-text">
              Sign in to access your profile
            </h2>
            <p className="mt-2 text-sm text-femty-muted">
              Manage your account, address, and preferences after signing in.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
              <button
                type="button"
                onClick={() => onOpenAuth("login")}
                className="rounded-lg border border-slate-200 bg-white px-6 py-2.5 text-sm font-semibold text-femty-text hover:bg-femty-bg"
              >
                Sign In
              </button>
              <button
                type="button"
                onClick={() => onOpenAuth("signup")}
                className="rounded-lg bg-[#7352C7] px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#7352C7]-hover"
              >
                Sign Up
              </button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="settings-page-root min-h-screen flex flex-col bg-femty-bg text-femty-text">
      <SettingsPageStyles />
      <Header onOpenAuth={onOpenAuth} onOpenMenu={onOpenMenu} />

      <main className="mx-auto w-full flex max-w-7xl flex-1 gap-10 px-4 py-10 md:px-10 min-h-[calc(100vh-120px)]">
        <aside className="w-56 rounded-2xl border border-slate-200 bg-white px-5 py-6 text-xs">
          <h2 className="mb-6 text-[18px] font-semibold text-femty-text">
            Settings
          </h2>

          <nav className="space-y-2 text-[13px]">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => {
                  setActiveTab(tab.id);
                  if (tab.id === "grocery") {
                    setGroceryView("lists");
                  }
                }}
                className={`flex w-full items-center rounded-full px-4 py-2 text-left transition ${activeTab === tab.id
                  ? "bg-[#7352C7] text-white"
                  : "bg-transparent text-femty-muted hover:bg-femty-bg"
                  }`}
              >
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </aside>

        <section className="flex-1 space-y-6">
          {activeTab === "public" && (
            <PublicProfilePanel onOpenChangePassword={() => setChangePasswordOpen(true)} />
          )}
          {activeTab === "billing" && <BillingPlansPanel />}
          {activeTab === "grocery" && (
            <GrocerySettingsPanel
              view={groceryView}
              previousView={groceryPrevView}
              onChangeView={(nextView) => {
                setGroceryPrevView(groceryView);
                setGroceryView(nextView);
              }}
              selectedId={selectedGroceryListId}
              onSelectId={setSelectedGroceryListId}
            />
          )}
        </section>
      </main>

      <Footer />

      <ChangePasswordOverlay
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />
    </div>
  );
}

function PanelShell({ title, action, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-7 py-6 h-full">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-lg font-semibold text-slate-900">{title}</h1>
        {action}
      </div>
      {children}
    </div>
  );
}

function PublicProfilePanel({ onOpenChangePassword }) {
  const user = useAppSelector((s) => s.auth.user);
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [imageError, setImageError] = useState("");
  const [imageLoading, setImageLoading] = useState(false);
  const [showDeleteImageConfirm, setShowDeleteImageConfirm] = useState(false);
  const [imageCacheBust, setImageCacheBust] = useState(0);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (user) {
      setFullName(user.full_name || "");
      setEmail(user.email || "");
      setAddressLine1(user.address_line1 || "");

    }
  }, [user]);

  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState("");

  async function handleSave() {
    setSaveError("");
    setSaveLoading(true);
    try {
      await dispatch(
        updateProfile({
          full_name: fullName.trim(),
          address_line1: addressLine1.trim() || null,

        })
      ).unwrap();
      showToast({
        type: "success",
        title: "Profile Updated",
        message: "Your profile details were saved successfully.",
      });
    } catch (err) {
      const message = typeof err === "string" ? err : "Failed to update profile.";
      setSaveError(message);
      showToast({ type: "error", title: "Update Failed", message });
    } finally {
      setSaveLoading(false);
    }
  }

  async function handleImageChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageError("");
    setImageLoading(true);
    try {
      const formData = new FormData();
      formData.append("image", file);

      const res = await fetch(`${API_BASE_URL}api/profile/image`, {
        method: "POST",
        headers: {
          ...getAuthHeader(),
        },
        body: formData,
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const msg = Array.isArray(data?.errors)
          ? data.errors.join("\n")
          : data?.message || "Failed to update profile image.";
        setImageError(msg);
        showToast({ type: "error", title: "Image Upload Failed", message: msg });
      } else {
        setImageCacheBust(Date.now());
        dispatch(fetchCurrentUser({ force: true }));
        showToast({
          type: "success",
          title: "Picture Updated",
          message: "Profile picture updated successfully.",
        });
      }
    } catch (err) {
      setImageError("Network error. Please try again.");
      showToast({
        type: "error",
        title: "Network Error",
        message: "Could not upload image. Please try again.",
      });
    } finally {
      setImageLoading(false);
      if (e.target) e.target.value = "";
    }
  }

  async function handleDeleteImage() {
    setImageError("");
    setImageLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}api/profile/image`, {
        method: "DELETE",
        headers: {
          ...getAuthHeader(),
        },
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const msg = Array.isArray(data?.errors)
          ? data.errors.join("\n")
          : data?.message || "Failed to delete profile image.";
        setImageError(msg);
        showToast({ type: "error", title: "Delete Failed", message: msg });
      } else {
        setImageCacheBust(Date.now());
        dispatch(fetchCurrentUser({ force: true }));
        showToast({
          type: "success",
          title: "Picture Deleted",
          message: "Profile picture deleted successfully.",
        });
      }
    } catch (err) {
      setImageError("Network error. Please try again.");
      showToast({
        type: "error",
        title: "Network Error",
        message: "Could not delete image. Please try again.",
      });
    } finally {
      setImageLoading(false);
    }
  }

  return (
    <PanelShell
      title="Public Profile"
      action={
        <button
          type="button"
          onClick={onOpenChangePassword}
          className="rounded-full bg-[#7352C7] px-5 py-2 text-xs font-semibold text-white hover:bg-[#5f43a8] flex items-center gap-1"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-4 h-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          >
            <rect
              x="5"
              y="11"
              width="14"
              height="10"
              rx="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M8 11V8a4 4 0 118 0v3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>

          Change Password
        </button>
      }
    >
      <div className="flex flex-col gap-8">
        <div className="flex flex-row items-center gap-3">
          <div className="flex h-24 w-24 items-center justify-center rounded-full bg-slate-100 text-sm font-semibold text-slate-500 overflow-hidden">
            {user?.image_url ? (
              <img
                src={
                  (() => {
                    const raw = user.image_url.startsWith("http")
                      ? user.image_url
                      : `${API_BASE_URL.replace(/\/$/, "")}${user.image_url}`;
                    const hasQuery = raw.includes("?");
                    return `${raw}${hasQuery ? "&" : "?"}v=${imageCacheBust || 0}`;
                  })()
                }
                alt={user.full_name || "Profile"}
                className="h-full w-full object-cover"
              />
            ) : (
              <span>IMG</span>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleImageChange}
          />
          <div className="space-y-2 text-xs">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={imageLoading}
              className="block rounded-md border border-slate-300 bg-[#7352C7] px-4 py-1.5 text-[11px] font-semibold text-white hover:bg-[#7352C7]-hover disabled:cursor-not-allowed disabled:opacity-60 flex items-center gap-1.5"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M12 12V4M8 8l4-4 4 4"
                />
              </svg>
              {imageLoading ? "Uploading..." : "Change picture"}
            </button>
            <button
              type="button"
              onClick={() => setShowDeleteImageConfirm(true)}
              disabled={imageLoading}
              className="block rounded-md border border-slate-300 px-4 py-1.5 text-[11px] font-semibold text-slate-800 disabled:cursor-not-allowed disabled:opacity-60 flex items-center gap-1.5"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 7h12M9 7V5h6v2M10 11v6M14 11v6M5 7l1 14h12l1-14"
                />
              </svg>

              Delete picture
            </button>
            {imageError && (
              <p className="mt-1 max-w-xs text-[11px] text-red-600">
                {imageError}
              </p>
            )}
          </div>
        </div>

        <div className="flex-1 space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1 text-xs">
              <label className="block text-[12px] font-medium text-slate-600">
                Full Name
              </label>
              <input
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-[13px] text-slate-800 outline-none focus:border-femty-primary"
                placeholder="Full Name"
              />
            </div>
            <div className="space-y-1 text-xs">
              <label className="block text-[12px] font-medium text-slate-600">
                Email
              </label>
              <input
                value={email}
                readOnly
                className="w-full cursor-not-allowed rounded-md border border-slate-300 bg-slate-100 px-3 py-2 text-[13px] text-slate-600 outline-none"
                placeholder="Email"
              />
            </div>
            <div className="space-y-1 text-xs md:col-span-2">
              <label className="block text-[12px] font-medium text-slate-600">
                Address
              </label>
              <input
                value={addressLine1}
                onChange={(e) => setAddressLine1(e.target.value)}
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-[13px] text-slate-800 outline-none focus:border-femty-primary"
                placeholder="Enter full address"
              />
            </div>

          </div>
        </div>
      </div>

      <div className="mt-8 space-y-2">
        {saveError && (
          <p className="text-xs text-red-600">{saveError}</p>
        )}
        <button
          type="button"
          onClick={handleSave}
          disabled={saveLoading}
          className="rounded-md bg-[#7352C7] px-8 py-2 text-xs font-semibold text-white hover:bg-[#7352C7]-hover disabled:opacity-60"
        >
          {saveLoading ? "Saving…" : "Save"}
        </button>
      </div>

      {showDeleteImageConfirm && (
        <div className="fixed inset-0 z-50">
          <button
            type="button"
            aria-label="Close delete confirmation"
            className="absolute inset-0 bg-black/40"
            onClick={() => setShowDeleteImageConfirm(false)}
          />
          <div className="relative mx-auto flex min-h-screen max-w-6xl items-center justify-center px-4 py-10">
            <div className="relative w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-xl">
              <h3 className="text-lg font-semibold text-slate-900">Delete Picture?</h3>
              <p className="mt-2 text-sm text-slate-600">
                Are you sure you want to delete your profile picture?
              </p>
              <div className="mt-6 flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowDeleteImageConfirm(false)}
                  className="flex-1 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={imageLoading}
                  onClick={async () => {
                    await handleDeleteImage();
                    setShowDeleteImageConfirm(false);
                  }}
                  className="flex-1 rounded-md bg-[#E44A77] px-4 py-2 text-sm font-semibold text-white hover:bg-[#cf3f69] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {imageLoading ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </PanelShell>
  );
}

function BillingPlansPanel() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const plans = useAppSelector((s) => s.plans.items);
  const status = useAppSelector((s) => s.plans.status);
  const subscriptionState = useAppSelector((s) => s.billing?.subscription);
  const billingStatus = useAppSelector((s) => s.billing?.status);
  const billingError = useAppSelector((s) => s.billing?.error);
  const token = useAppSelector((s) => s.auth.token);

  useEffect(() => {
    if (status === "idle") dispatch(fetchPlans());
  }, [status, dispatch]);

  useEffect(() => {
    if (!token) return;
    dispatch(fetchCurrentSubscription({ force: true }));
  }, [token, dispatch]);

  function formatPlanPrice(amountCents) {
    const cents = Number(amountCents ?? 0);
    if (Number.isNaN(cents)) return "£0";
    const amount = cents / 100;
    if (amount === 0) return "Free";
    return `£${amount.toFixed(2).replace(/\\.00$/, "")}`;
  }

  function isCurrentPlan(subscriptionPlan, plan) {
    if (!subscriptionPlan) return false;
    const sp = String(subscriptionPlan).toLowerCase();

    const candidates = [plan?.id, plan?.name, plan?.plan_title]
      .filter((v) => v !== undefined && v !== null)
      .map((v) => String(v).toLowerCase());

    return candidates.some((c) => c === sp || c.includes(sp) || sp.includes(c));
  }

  function isFreeOrTrialPlan(plan) {
    if (!plan) return false;
    if (plan.is_trial) return true;
    const cents = Number(plan.amount_cents ?? 0);
    return !Number.isNaN(cents) && cents <= 0;
  }

  const currentPlanObj =
    Array.isArray(plans) && subscriptionState?.plan
      ? plans.find((p) => isCurrentPlan(subscriptionState.plan, p))
      : null;

  function formatDateMaybe(value) {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString();
  }

  const currentPlanName =
    currentPlanObj?.name ||
    currentPlanObj?.plan_title ||
    subscriptionState?.plan ||
    "Plan";

  const currentInterval = currentPlanObj?.interval || "";
  const currentIntervalLabel =
    currentInterval === "day"
      ? "Daily"
      : currentInterval === "month"
        ? "Monthly"
        : currentInterval
          ? String(currentInterval)
          : "";

  const isFreeOrTrialCurrent = isFreeOrTrialPlan(currentPlanObj);
  const nextPaymentDate = formatDateMaybe(subscriptionState?.current_period_end);
  const currentAmountLabel =
    currentPlanObj && typeof currentPlanObj?.amount_cents !== "undefined"
      ? formatPlanPrice(currentPlanObj.amount_cents)
      : "";

  const currentPlanLine = (() => {
    if (billingStatus === "loading") return "Loading subscription…";
    if (billingStatus === "failed") return billingError || "Failed to load subscription.";

    if (!subscriptionState) return "No active subscription found.";

    const left =
      currentIntervalLabel
        ? `Billing ${currentIntervalLabel}`
        : subscriptionState?.status
          ? `Status: ${subscriptionState.status}`
          : "Subscription";

    if (isFreeOrTrialCurrent) {
      if (nextPaymentDate) return `${left} | Ends on ${nextPaymentDate}`;
      return `${left} | No renewal`;
    }

    if (nextPaymentDate && currentAmountLabel) {
      return `${left} | Next Payment on ${nextPaymentDate} for ${currentAmountLabel}`;
    }

    if (nextPaymentDate) return `${left} | Next Payment on ${nextPaymentDate}`;
    if (currentAmountLabel) return `${left} | Amount ${currentAmountLabel}`;
    return left;
  })();

  return (
    <PanelShell
      title="Billings & Plans"
      action={
        <></>
      }
    >
      <div className="space-y-6 text-xs text-slate-600">
        <div className="flex justify-between items-center rounded-xl border border-slate-200 bg-white px-5 py-5">
          <div>
            <p className="text-[12px] font-semibold text-slate-700">
              {currentPlanName}
            </p>
            <p className="mt-1 text-[11px] text-slate-500">{currentPlanLine}</p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/subscription")}
            className="rounded-md bg-[#7352C7] px-5 py-2 text-xs font-semibold text-white hover:bg-[#7352C7]-hover disabled:opacity-60"
          >
            Change Plan
          </button>
        </div>

        <div>
          <p className="mb-3 text-[12px] font-semibold text-slate-700">
            Transaction history
          </p>
          <div className="overflow-hidden rounded-xl border border-slate-200 text-[12px]">
            <div className="grid grid-cols-5 bg-slate-50 px-4 py-2 font-semibold text-slate-600">
              <span>Reference</span>
              <span>Product</span>
              <span>Status</span>
              <span>Date</span>
              <span className="text-right">Amount</span>
            </div>
            {["#95687"].map((id) => (
              <div
                key={id}
                className="grid grid-cols-5 items-center border-t border-slate-100 px-4 py-2 text-slate-600"
              >
                <span>{id}</span>
                <span>Standard</span>
                <span className="text-emerald-500">Paid</span>
                <span>02/02/2026</span>
                <span className="text-right">£29.99</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </PanelShell>
  );
}

function GrocerySettingsPanel({ view, previousView, onChangeView, selectedId, onSelectId }) {
  if (view === "detail") {
    return (
      <GroceryDetail
        id={selectedId}
        onBack={() => {
          onSelectId?.(null);
          onChangeView(previousView === "detail" ? "lists" : previousView || "lists");
        }}
      />
    );
  }

  if (view === "create") {
    return (
      <GroceryListsWithCreate
        onCancel={() => onChangeView("lists")}
      />
    );
  }

  return (
    <GroceryListsOverview
      onOpenCreate={() => onChangeView("create")}
      onOpenDetail={(list) => {
        onSelectId?.(list?.id ?? null);
        onChangeView("detail");
      }}
    />
  );
}

function GroceryListsContent({ lists, status, error, onOpenDetail }) {
  if (status === "loading") {
    return (
      <div className="flex min-h-[360px] items-center justify-center py-10">
        <span className="text-xs text-slate-500">Loading…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[360px] items-center justify-center py-6 text-center">
        <p className="text-xs text-red-600 whitespace-pre-line">{error}</p>
      </div>
    );
  }

  if (!lists || lists.length === 0) {
    return (
      <div className="flex min-h-[360px] items-center justify-center py-10 text-center text-xs text-slate-500">
        No grocery lists yet.
      </div>
    );
  }

  return (
    <div className="grid min-h-[200px] grid-cols-1 gap-4 sm:grid-cols-3">
      {lists.map((list) => {
        const latestImages = Array.isArray(list?.latest_product_images)
          ? list.latest_product_images.filter(Boolean).slice(0, 4)
          : [];
        return (
          <button
            key={list.id}
            type="button"
            onClick={() => onOpenDetail?.(list)}
            className="flex flex-col items-stretch rounded-2xl border border-slate-200 bg-white p-3 text-left hover:border-femty-primary"
          >
            <div className="mb-4 grid h-36 grid-cols-2 grid-rows-2 gap-3 overflow-hidden rounded-xl bg-white p-1">
              {[0, 1, 2, 3].map((index) => (
                <div key={`${list.id}-img-${index}`} className="h-full w-full overflow-hidden rounded-md bg-slate-200">
                  {latestImages[index] ? (
                    <img
                      src={latestImages[index]}
                      alt={`${list.title || "List"} item ${index + 1}`}
                      className="h-full w-full object-contain"
                      loading="lazy"
                    />
                  ) : (
                    <div className="h-full w-full bg-slate-200" />
                  )}
                </div>
              ))}
            </div>
            <p className="text-sm font-semibold text-slate-800">
              {list.title || "N/A"}
            </p>
            <div className="mt-2 flex items-center justify-between">
              <p className="text-[12px] text-slate-600">
                Budget - £{list.budget ? list.budget : 0}
              </p>
              <p className="text-[12px] text-slate-600">
               Products - {list.total_products_count ? list.total_products_count : 0}
              </p>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function GroceryListsOverview({ onOpenCreate, onOpenDetail }) {
  const dispatch = useAppDispatch();
  const lists = useAppSelector((s) => s.groceryLists.items);
  const status = useAppSelector((s) => s.groceryLists.fetchStatus);
  const error = useAppSelector((s) => s.groceryLists.fetchError);

  useEffect(() => {
    if (status === "idle") dispatch(fetchGroceryLists());
  }, [status, dispatch]);

  return (
    <PanelShell
      title="My Grocery Lists"
      action={
        <button
          className="rounded-md bg-[#7352C7] px-5 py-2 text-xs font-semibold text-white hover:bg-[#7352C7]-hover"
          onClick={onOpenCreate}
        >
          + Create List
        </button>
      }
    >
      <GroceryListsContent
        lists={lists}
        status={status}
        error={error}
        onOpenDetail={onOpenDetail}
      />
    </PanelShell>
  );
}

function GroceryListsWithCreate({ onCancel }) {
  const dispatch = useAppDispatch();
  const createStatus = useAppSelector((s) => s.groceryLists.createStatus);
  const createError = useAppSelector((s) => s.groceryLists.createError);
  const [title, setTitle] = useState("");
  const [budget, setBudget] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    dispatch(clearCreateGroceryListState());
    setTitle("");
    setBudget("");
    setFieldErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function validate() {
    const errs = {};
    if (!title.trim()) errs.title = "Title is required.";
    if (budget !== "" && Number.isNaN(Number(budget))) {
      errs.budget = "Budget must be a number.";
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleCreate() {
    if (!validate()) return;
    const payload = {
      title: title.trim(),
      budget: budget === "" ? undefined : Number(budget),
    };
    try {
      await dispatch(createGroceryList(payload)).unwrap();
      // Ensure list is fresh even if API doesn't return object
      dispatch(fetchGroceryLists({ force: true }));
      onCancel?.(); // close popup back to lists view
    } catch (e) {
      // error shown from createError
    }
  }

  return (
    <PanelShell
      title="My Grocery Lists"
      action={
        <button
          type="button"
          className="rounded-md bg-[#7352C7] px-5 py-2 text-xs font-semibold text-white hover:bg-[#7352C7]-hover"
        >
          + Create List
        </button>
      }
    >
      <div className="fixed inset-0 z-50">
        <button
          type="button"
          aria-label="Close create list popup"
          className="absolute inset-0 bg-black/40"
          onClick={onCancel}
        />
        <div className="relative mx-auto flex min-h-screen max-w-6xl items-center justify-center px-4 py-10">
          <div className="relative w-full max-w-md rounded-3xl border border-slate-200 bg-white shadow-2xl">
            <div className="relative flex items-center justify-center px-6 pt-6">
              <p className="text-[18px] md:text-[20px] font-semibold text-femty-text">
                Create List
              </p>
              <button
                type="button"
                onClick={onCancel}
                className="absolute right-6 inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white p-0 text-slate-500 hover:bg-[#7352C7]/10 hover:text-femty-primary"
              >
                ✕
              </button>
            </div>

            <div className="px-6 pb-6 pt-4">
              {!!createError && (
                <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 whitespace-pre-line">
                  {createError}
                </div>
              )}

              <div className="space-y-4">
                <div className="space-y-1">
                  <input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-[14px] font-normal text-femty-text shadow-sm focus:border-femty-primary focus:outline-none"
                    placeholder="List title"
                  />
                  {fieldErrors.title && (
                    <p className="mt-1 text-xs text-red-600">{fieldErrors.title}</p>
                  )}
                </div>

                <div className="space-y-1">
                  <input
                    value={budget}
                    onChange={(e) => setBudget(e.target.value)}
                    inputMode="numeric"
                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-[14px] font-normal text-femty-text shadow-sm focus:border-femty-primary focus:outline-none"
                    placeholder="Budget"
                  />
                  {fieldErrors.budget && (
                    <p className="mt-1 text-xs text-red-600">{fieldErrors.budget}</p>
                  )}
                </div>

                <button
                  type="button"
                  onClick={handleCreate}
                  disabled={createStatus === "loading"}
                  className="w-full rounded-xl bg-femty-primary py-3 text-[14px] font-bold text-white hover:bg-femty-primary-hover disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {createStatus === "loading" ? "Saving..." : "Create List"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PanelShell>
  );
}

function GroceryDetail({ id, onBack }) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const detail = useAppSelector((s) => s.groceryLists.detail);
  const detailStatus = useAppSelector((s) => s.groceryLists.detailStatus);
  const detailError = useAppSelector((s) => s.groceryLists.detailError);
  const updateStatus = useAppSelector((s) => s.groceryLists.updateStatus);
  const updateError = useAppSelector((s) => s.groceryLists.updateError);
  const deleteStatus = useAppSelector((s) => s.groceryLists.deleteStatus);
  const deleteError = useAppSelector((s) => s.groceryLists.deleteError);
  const addProductStatus = useAppSelector((s) => s.groceryLists.addProductStatus);
  const addProductError = useAppSelector((s) => s.groceryLists.addProductError);
  const updateProductStatus = useAppSelector((s) => s.groceryLists.updateProductStatus);
  const updateProductError = useAppSelector((s) => s.groceryLists.updateProductError);
  const deleteProductStatus = useAppSelector((s) => s.groceryLists.deleteProductStatus);
  const deleteProductError = useAppSelector((s) => s.groceryLists.deleteProductError);
  const [renameOpen, setRenameOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [addProductOpen, setAddProductOpen] = useState(false);
  const [productId, setProductId] = useState("");
  const [productImageUrl, setProductImageUrl] = useState("");
  const [productPrice, setProductPrice] = useState("");
  const [productWeight, setProductWeight] = useState("");
  const [productQty, setProductQty] = useState(1);
  const [title, setTitle] = useState("");
  const [budget, setBudget] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    dispatch(clearUpdateGroceryListState());
    dispatch(clearDeleteGroceryListState());
    dispatch(clearAddGroceryProductState());
    dispatch(clearUpdateGroceryProductState());
    dispatch(clearDeleteGroceryProductState());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!id) return;
    dispatch(fetchGroceryListDetail({ id }));
  }, [id, dispatch]);

  useEffect(() => {
    if (!detail || detail?.id !== id) return;
    setTitle(detail.title || "");
    setBudget(
      typeof detail.budget === "number" || typeof detail.budget === "string"
        ? String(detail.budget)
        : ""
    );
  }, [detail, id]);

  function validateRename() {
    const errs = {};
    if (!title.trim()) errs.title = "Title is required.";
    if (budget !== "" && Number.isNaN(Number(budget))) errs.budget = "Budget must be a number.";
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleUpdate() {
    if (!id) return;
    if (!validateRename()) return;
    const payload = {
      id,
      title: title.trim(),
      budget: budget === "" ? undefined : Number(budget),
    };
    try {
      await dispatch(updateGroceryList(payload)).unwrap();
      dispatch(fetchGroceryListDetail({ id, force: true }));
      dispatch(fetchGroceryLists({ force: true }));
      setRenameOpen(false);
    } catch (e) {
      // shown in updateError
    }
  }

  async function handleDelete() {
    if (!id) return;
    try {
      await dispatch(deleteGroceryList({ id })).unwrap();
      dispatch(fetchGroceryLists({ force: true }));
      setConfirmDeleteOpen(false);
      onBack?.();
    } catch (e) {
      // shown in deleteError
    }
  }

  const products =
    detail?.products ||
    detail?.items ||
    detail?.grocery_list_products ||
    detail?.grocery_products ||
    [];

  return (
    <PanelShell
      title="Grocery Detail"
      action={
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onBack?.()}
            className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm hover:bg-[#F5F7FB]"
          >
            <svg
              viewBox="0 0 24 24"
              className="h-4 w-4 shrink-0"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>
            <span className="leading-none">Back</span>
          </button>
          <button
            type="button"
            onClick={() => {
              setRenameOpen(true);
              dispatch(clearUpdateGroceryListState());
              setFieldErrors({});
            }}
            className="rounded-md border border-slate-200 px-4 py-1.5 text-xs font-medium text-white bg-[#7352C7] hover:bg-[#7352C7]-hover"
          >
            Rename
          </button>
          <button
            type="button"
            onClick={() => {
              setConfirmDeleteOpen(true);
              dispatch(clearDeleteGroceryListState());
            }}
            className="rounded-md bg-red-500 px-4 py-1.5 text-xs font-semibold text-white"
          >
            Delete
          </button>
        </div>
      }
    >
      {detailStatus === "loading" ? (
        <div className="flex min-h-[280px] items-center justify-center">
          <span className="text-xs text-slate-500">Loading…</span>
        </div>
      ) : detailError ? (
        <div className="py-6 text-center">
          <p className="text-xs text-red-600 whitespace-pre-line">{detailError}</p>
        </div>
      ) : !detail ? (
        <div className="py-10 text-center text-xs text-slate-500">
          Select a list to view details.
        </div>
      ) : (
        <>
          <div className="mb-4 text-xs text-slate-600">
            <p className="text-[18px] font-semibold text-slate-800">
              {detail.title || "Untitled"}
            </p>
            <p className="mt-2 text-[12px] font-semibold text-slate-500">
              {Array.isArray(products) ? products.length : 0}: Items
              {detail?.budget != null && String(detail.budget) !== "" ? ` | Budget: £${detail.budget}` : ""}
            </p>
          </div>

          {Array.isArray(products) && products.length > 0 ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              {products.map((p, idx) => (
                <div
                  key={p?.id ?? idx}
                  className="flex flex-col rounded-2xl border border-slate-200 bg-slate-50 p-3"
                >
                  <div className="mb-3 flex h-24 items-center justify-center overflow-hidden rounded-xl bg-slate-100">
                    {p?.image_url ? (
                      <img
                        src={p.image_url}
                        alt={p?.title || p?.name || p?.product_id || "Product"}
                        className="h-full w-full object-contain"
                      />
                    ) : (
                      <span className="text-[11px] text-slate-400">No image</span>
                    )}
                  </div>
                  <p className="text-xs font-semibold text-slate-800">
                    {p?.title || p?.name || p?.product_id || "Product"}
                  </p>
                  <p className="mt-1 text-[11px] text-slate-500">
                    Weight: {p?.weight || p?.packsize || ""}
                  </p>
                  <div className="mt-3 flex items-center justify-between text-[11px]">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        disabled={updateProductStatus === "loading"}
                        className="h-7 w-7 rounded-full border border-slate-200 bg-white text-slate-700"
                        onClick={async () => {
                          const nextQty = Math.max(1, Number(p?.quantity || 1) - 1);
                          try {
                            await dispatch(
                              updateGroceryProduct({
                                grocery_list_id: id,
                                id: p.id,
                                product_id: p.product_id,
                                image_url: p.image_url,
                                price: p.price,
                                weight: p.weight,
                                quantity: nextQty,
                              })
                            ).unwrap();
                            dispatch(fetchGroceryListDetail({ id, force: true }));
                          } catch (e) {
                            // shown via updateProductError
                          }
                        }}
                      >
                        −
                      </button>
                      <span className="min-w-6 text-center font-semibold text-slate-800">
                        {p?.quantity ?? 1}
                      </span>
                      <button
                        type="button"
                        disabled={updateProductStatus === "loading"}
                        className="h-7 w-7 rounded-full border border-slate-200 bg-white text-slate-700"
                        onClick={async () => {
                          const nextQty = Number(p?.quantity || 1) + 1;
                          try {
                            await dispatch(
                              updateGroceryProduct({
                                grocery_list_id: id,
                                id: p.id,
                                product_id: p.product_id,
                                image_url: p.image_url,
                                price: p.price,
                                weight: p.weight,
                                quantity: nextQty,
                              })
                            ).unwrap();
                            dispatch(fetchGroceryListDetail({ id, force: true }));
                          } catch (e) {
                            // shown via updateProductError
                          }
                        }}
                      >
                        +
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 hover:bg-slate-100"
                        onClick={() => {
                          const productId = p?.product_id ?? p?.id;
                          if (productId) {
                            navigate(`/product/${encodeURIComponent(String(productId))}`);
                          }
                        }}
                        aria-label="View product"
                        title="View product"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          className="h-4 w-4"
                        >
                          <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        disabled={deleteProductStatus === "loading"}
                        className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-red-500 text-white disabled:opacity-60"
                        onClick={async () => {
                          try {
                            await dispatch(
                              deleteGroceryProduct({ grocery_list_id: id, id: p.id })
                            ).unwrap();
                            dispatch(fetchGroceryListDetail({ id, force: true }));
                          } catch (e) {
                            // shown via deleteProductError
                          }
                        }}
                        aria-label="Delete product"
                        title="Delete product"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          className="h-4 w-4"
                        >
                          <path d="M3 6h18" />
                          <path d="M8 6V4h8v2" />
                          <path d="M19 6l-1 14H6L5 6" />
                          <path d="M10 11v6M14 11v6" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="min-h-[220px] flex items-center justify-center text-xs text-slate-500">
              No products in this list yet.
            </div>
          )}
        </>
      )}

      {/* Rename popup */}
      {renameOpen && (
        <div className="fixed inset-0 z-50">
          <button
            type="button"
            className="absolute inset-0 bg-black/30"
            onClick={() => setRenameOpen(false)}
            aria-label="Close"
          />
          <div className="relative mx-auto flex min-h-screen max-w-6xl items-center justify-center px-4 py-10">
            <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white px-8 py-10 text-xs shadow-xl">
              <div className="mb-4 flex items-center justify-between">
                <p className="text-[14px] font-semibold text-slate-900">
                  Update List
                </p>
                <button
                  type="button"
                  onClick={() => setRenameOpen(false)}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white p-0 text-slate-500 hover:bg-[#F5F7FB] hover:text-slate-800"
                >
                  ✕
                </button>
              </div>

              {!!updateError && (
                <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 whitespace-pre-line">
                  {updateError}
                </div>
              )}

              <div className="space-y-5">
                <div className="space-y-1">
                  <input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-[13px] text-slate-800 outline-none focus:border-femty-primary"
                    placeholder="Title"
                  />
                  {fieldErrors.title && (
                    <p className="text-xs text-red-600">{fieldErrors.title}</p>
                  )}
                </div>
                <div className="space-y-1">
                  <input
                    value={budget}
                    onChange={(e) => setBudget(e.target.value)}
                    inputMode="numeric"
                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-[13px] text-slate-800 outline-none focus:border-femty-primary"
                    placeholder="Budget"
                  />
                  {fieldErrors.budget && (
                    <p className="text-xs text-red-600">{fieldErrors.budget}</p>
                  )}
                </div>

                <div className="pt-2 flex items-center justify-center gap-3">
                  <button
                    type="button"
                    onClick={handleUpdate}
                    disabled={updateStatus === "loading"}
                    className="rounded-md bg-[#7352C7] px-8 py-2 text-sm font-semibold text-white hover:bg-[#7352C7]-hover disabled:opacity-60"
                  >
                    {updateStatus === "loading" ? "Saving..." : "Save →"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm popup */}
      {confirmDeleteOpen && (
        <div className="fixed inset-0 z-50">
          <button
            type="button"
            className="absolute inset-0 bg-black/30"
            onClick={() => setConfirmDeleteOpen(false)}
            aria-label="Close"
          />
          <div className="relative mx-auto flex min-h-screen max-w-6xl items-center justify-center px-4 py-10">
            <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white px-8 py-8 text-xs shadow-xl">
              <div className="mb-2 text-[15px] font-semibold text-slate-900">
                Delete list?
              </div>
              <p className="text-sm text-slate-600">
                This action can’t be undone.
              </p>

              {!!deleteError && (
                <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 whitespace-pre-line">
                  {deleteError}
                </div>
              )}

              <div className="mt-6 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setConfirmDeleteOpen(false)}
                  className="rounded-md border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-800"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleteStatus === "loading"}
                  className="rounded-md bg-red-500 px-4 py-2 text-xs font-semibold text-white disabled:opacity-60"
                >
                  {deleteStatus === "loading" ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add product popup */}
      {addProductOpen && (
        <div className="fixed inset-0 z-50">
          <button
            type="button"
            className="absolute inset-0 bg-black/30"
            onClick={() => setAddProductOpen(false)}
            aria-label="Close"
          />
          <div className="relative mx-auto flex min-h-screen max-w-6xl items-center justify-center px-4 py-10">
            <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white px-8 py-10 text-xs shadow-xl">
              <div className="mb-4 flex items-center justify-between">
                <p className="text-[14px] font-semibold text-slate-900">
                  Add Product
                </p>
                <button
                  type="button"
                  onClick={() => setAddProductOpen(false)}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white p-0 text-slate-500 hover:bg-[#F5F7FB] hover:text-slate-800"
                >
                  ✕
                </button>
              </div>

              {!!addProductError && (
                <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 whitespace-pre-line">
                  {addProductError}
                </div>
              )}
              {!!updateProductError && (
                <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 whitespace-pre-line">
                  {updateProductError}
                </div>
              )}
              {!!deleteProductError && (
                <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 whitespace-pre-line">
                  {deleteProductError}
                </div>
              )}

              <div className="space-y-5">
                <div className="space-y-1">
                  <input
                    value={productId}
                    onChange={(e) => setProductId(e.target.value)}
                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-[13px] text-slate-800 outline-none focus:border-femty-primary"
                    placeholder="Product ID (SKU)"
                  />
                  {fieldErrors.product_id && (
                    <p className="text-xs text-red-600">{fieldErrors.product_id}</p>
                  )}
                </div>
                <div className="space-y-1">
                  <input
                    value={productImageUrl}
                    onChange={(e) => setProductImageUrl(e.target.value)}
                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-[13px] text-slate-800 outline-none focus:border-femty-primary"
                    placeholder="Image URL"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <input
                      value={productPrice}
                      onChange={(e) => setProductPrice(e.target.value)}
                      inputMode="decimal"
                      className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-[13px] text-slate-800 outline-none focus:border-femty-primary"
                      placeholder="Price"
                    />
                  </div>
                  <div className="space-y-1">
                    <input
                      value={productWeight}
                      onChange={(e) => setProductWeight(e.target.value)}
                      className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-[13px] text-slate-800 outline-none focus:border-femty-primary"
                      placeholder="Weight"
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <input
                    value={String(productQty)}
                    onChange={(e) =>
                      setProductQty(Math.max(1, Number(e.target.value || 1)))
                    }
                    inputMode="numeric"
                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-[13px] text-slate-800 outline-none focus:border-femty-primary"
                    placeholder="Quantity"
                  />
                </div>

                <div className="pt-2 flex items-center justify-center gap-3">
                  <button
                    type="button"
                    disabled={addProductStatus === "loading"}
                    onClick={async () => {
                      const errs = {};
                      if (!productId.trim()) errs.product_id = "Product ID is required.";
                      setFieldErrors(errs);
                      if (Object.keys(errs).length > 0) return;

                      const parsedPrice =
                        productPrice === "" ? undefined : Number(productPrice);

                      try {
                        await dispatch(
                          addGroceryProduct({
                            grocery_list_id: id,
                            product_id: productId.trim(),
                            image_url: productImageUrl.trim() || undefined,
                            price: Number.isNaN(parsedPrice) ? undefined : parsedPrice,
                            weight: productWeight.trim() || undefined,
                            quantity: productQty || 1,
                          })
                        ).unwrap();
                        dispatch(fetchGroceryListDetail({ id, force: true }));
                        setAddProductOpen(false);
                      } catch (e) {
                        // shown via addProductError
                      }
                    }}
                    className="rounded-md bg-[#7352C7] px-8 py-2 text-sm font-semibold text-white hover:bg-[#7352C7]-hover disabled:opacity-60"
                  >
                    {addProductStatus === "loading" ? "Adding..." : "Add →"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </PanelShell>
  );
}

export default Settings;

